import searchApi from "@api/search";
import { Popover } from "@headlessui/react";
import React, { useRef, useState } from "react";
import {
  ISearchKeywordSuggestInput,
  ISearchSuggestInput,
  SuggestResult,
} from "types/api/search";
import cn from "classnames";
import SearchPopUp from "./search-pop-up";
import { RequestMeta } from "@types";
import useScroll from "@shared/hooks/useScroll";
import { useDebounce } from "@shared/hooks/useDebounce";
import { SearchInputWithLogo } from "@shared/components/search-input";

interface Props {
  onChange: (e: string) => void;
  onAction: (e: string) => void;
  resetValue: () => void;
  value: string;
  className?: string;
  renderInput?: React.ReactNode;
}

const MobileTopSearchbar: React.FC<Props> = ({
  onChange,
  onAction,
  value,
  resetValue,
  className = "",
  renderInput,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [suggestKeyword, setKeyword] = useState<string[]>([]);
  const [searchProductResult, setSearchProductResult] = useState<
    SuggestResult | []
  >([]);
  const [hasResult, setHasResult] = useState<boolean>(false);

  const autoCompleteApi =
    useRef<RequestMeta<ISearchKeywordSuggestInput, never, never>>(undefined);

  const api = useRef<RequestMeta<ISearchSuggestInput, never, never>>(undefined);

  const resetApi = () => {
    if (api.current) {
      api.current.cancel();
      api.current = undefined;
    }
    if (autoCompleteApi.current) {
      autoCompleteApi.current.cancel();
      autoCompleteApi.current = undefined;
    }
  };

  const debounceSearch = async (input: string) => {
    if (input.length < 2) {
      resetApi();
      setHasResult(false);
      setPending(false);
      setSearchProductResult([]);
      setKeyword([]);
      return;
    }

    if (input !== "") {
      setHasResult(true);
      setPending(true);
      search(input);
    }
  };

  const search = async (e: string) => {
    resetApi();

    api.current = searchApi.suggestSearch(e);
    autoCompleteApi.current = searchApi.suggestKeyword(e);

    try {
      const res = await api.current?.fetch();

      autoCompleteApi.current.fetch().then((data) => {
        if (data.status) setKeyword(data.data.keywords.map((d) => d.title));
        else setKeyword([e]);
      });

      if (res.status) {
        setPending(false);
        setSearchProductResult(res.data);
      } else {
        setKeyword([e]);
        setPending(false);
      }
    } catch (error: any) {
      if (!error?.canceled) {
        setKeyword([e]);
        setPending(false);
      }
    }
  };

  const { allowScroll, blockScroll } = useScroll();
  const onChangeDebounced = useDebounce(debounceSearch);

  const openSearch = () => {
    setOpen(true);
    blockScroll();
    onChangeDebounced(value);

    const el = inputRef.current;
    if (el) {
      el.focus({ preventScroll: true });
      const len = el.value.length;
      try {
        el.setSelectionRange(len, len);
      } catch {}
    }
  };
  const close = () => {
    setOpen(false);
    allowScroll();
  };
  return (
    <div className={cn("relative", className)}>
      <div className="w-full" onClick={openSearch} role="button">
        {renderInput || <SearchInputWithLogo defaultValue={value} />}
      </div>

      <SearchPopUp
        open={open}
        inputRef={inputRef}
        searchValue={value}
        onClickCancel={() => {
          resetValue();
          setHasResult(false);
        }}
        onChangeInput={(e) => {
          onChange(e);
          onChangeDebounced(e);
        }}
        onEnter={(e) => {
          onAction(e);
          close();
        }}
        onAction={onAction}
        closeHandler={close}
        searchProductResult={searchProductResult}
        hasResult={hasResult}
        pending={pending}
        suggestKeyword={suggestKeyword}
      />
    </div>
  );
};

export default MobileTopSearchbar;
